import i18n, { type BackendModule, type ResourceKey } from "i18next";
import { initReactI18next } from "react-i18next";
import { SUPPORTED_LOCALE_CODES, DEFAULT_LOCALE, localeFromPath } from "./locales";
import { isChunkLoadError, recoverFromChunkError } from "./chunkReload";
import en from "../locales/en/common.json";

// English (the default and every visitor's fallback) ships with the app; each
// other locale is its own chunk, fetched the first time that language is
// needed — a visitor downloads English plus their language, not all 17 (~3 MB).
// Only the app's entry imports this module — keep it that way (pages read the
// language through react-i18next or the `i18next` instance). A copy change
// renames the locale chunks and whatever imports this module, so a page that
// imported it would be renamed by every copy change too, and a tab left open
// across that deploy could no longer load it.
const localeModules = import.meta.glob<{ default: ResourceKey }>([
  "../locales/*/common.json",
  "!../locales/en/common.json",
]);

const localeBackend: BackendModule = {
  type: "backend",
  init() {},
  read(language, _namespace, callback) {
    const load = localeModules[`../locales/${language}/common.json`];
    if (!load) {
      callback(null, {});
      return;
    }
    load().then(
      (module) => callback(null, module.default),
      (error) => {
        // A tab older than the deploy asking for a locale chunk that is gone:
        // reload onto the new build rather than stay in the old language.
        if (isChunkLoadError(error)) recoverFromChunkError();
        callback(error, null);
      },
    );
  },
};

// The first-paint locale must match what the SSR renderer produced, or React
// hydration mismatches. Localized language pages (/xx/find-a-tutor/*) are
// SSR'd in their URL locale (#126), so initialise the client to that same locale
// when the URL carries one; every other route stays English (the SSR default).
// <LocaleSync> then switches to the user's real locale in a post-mount effect
// (signed-in `ui_locale` > anonymous stored choice > browser language).
const initialLng =
  (typeof window !== "undefined" && localeFromPath(window.location.pathname)) || DEFAULT_LOCALE;

// Resolves once the first-paint language is loaded — at once for English, after
// one chunk fetch for a localized page (/fr/find-a-tutor/*). The browser entry
// awaits it before its first render, so that render — and hydration of SSR
// HTML — has the same strings the server used.
export const i18nReady = i18n
  .use(localeBackend)
  .use(initReactI18next)
  .init({
    resources: { en: { common: en } },
    partialBundledLanguages: true, // the backend still loads the rest
    lng: initialLng,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: SUPPORTED_LOCALE_CODES,
    defaultNS: "common",
    ns: ["common"],
    interpolation: { escapeValue: false }, // React already escapes
    returnNull: false,
    react: { useSuspense: false },
  });

export default i18n;
